import React from 'react';
import { Formik } from 'formik';
import { Alert, Col, Collapse, DatePicker, Divider, Row, Spin, Tooltip, Typography } from 'antd';
import { Form, FormItem, Input, ResetButton, Select, SubmitButton, Switch } from 'formik-antd';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { InfoCircleOutlined } from '@ant-design/icons';
import isEqual from 'lodash/isEqual';
import { omit } from 'lodash/object';
import IntlMessages from '../../../../layout/components/lang/IntlMessages';
import { countries } from '../../../../const/countries';
import { dateFieldFormat, targetPlatformOptions } from '../../../../const/system';
import { Permissions } from '../../../../const/permissions';
import useDatepickerHandler from '../../../../app/hooks/useDatepickerHandler';
import mkTemplateDynamicFormData from '../../utils/mkTemplateDynamicFormData';
import DynamicFormDataFields from '../../components/DynamicFormDataFields';
import CountrySelector from '../../../../components/CountrySelector';
import TemplateVisualization from '../../../Template builder/components/TemplateVisualization';
import TitleWithAutomaticGeneration from '../../../../components/TitleWithAutomaticGeneration';
import mkMainAndOptionalTemplateFields from '../../../Template builder/utils/mkMainAndOptionalTemplateFields';
import Spinner from '../../../../components/Spinner';
import { PrivateComponent } from '../../../../components/HasRights/HasRights';
import applyPreset from '../../../../lib/applyPreset';
import isDateDisabled from '../../../../lib/isDateDisabled';
import mkTemplatesOptions from '../../utils/mkTemplatesOptions';
import { useGetPresetsQuery } from '../../../Template builder/api/presetsApiSlice';
import { useGetTemplatesVisualizationsQuery } from '../../../Template builder/api/templatesVisualizationsApiSlice';



const { Title } = Typography;
const { Panel } = Collapse;

const FormEditDynamicNews = ({
  initialErrors,
  initialValues,
  templateFields,
  templateOptions,
  validationSchema,
  onSubmit,
  onCancel,
  isSubmitting,
}) => {
  const intl = useIntl();
  const { onBlur } = useDatepickerHandler();
  const { mainFields, optionalFields } = mkMainAndOptionalTemplateFields(templateFields);
  const language = useSelector(({ customise: { language } }) => language);

  const { data: { data: presets } = { data: [] } } = useGetPresetsQuery({
    queryParams: `search=template_id:${initialValues?.template_id}`,
  }, { skip: !initialValues?.template_id });

  const {
    data: { data: visualizations } = { data: [] },
    isFetching: isLoadingVisualizations,
  } = useGetTemplatesVisualizationsQuery({
    queryParams: `search=template_id:${initialValues?.template_id}`,
  }, { skip: !initialValues?.template_id });

  if (!isEmpty(initialErrors)) {
    window.scrollTo(0, 0);
  }

  return (
    <Formik
      enableReinitialize
      isSubmitting
      initialErrors={initialErrors}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, { resetForm }) => {
        onSubmit(mkTemplateDynamicFormData(values, language, templateFields), resetForm);
      }}
      onReset={async (values) => {
        onCancel(values);
      }}
    >
      {({ isValid, values, setFieldValue, setFieldTouched, resetForm }) => {
        const hasUnpublishedChanges = !moment(values?.published_at).isSame(moment(values?.updated_at));
        const isDirty = !isEqual(
          omit(values, [ 'visualization', 'language' ]),
          omit(initialValues, [ 'visualization', 'language' ]),
        );

        return (
          <Form layout="vertical" >
            <Spinner spinning={isSubmitting}>
              <Title className="hp-mb-24" level={4}>{intl.formatMessage({ id: 'structures-source-news' })}</Title>

              <Row gutter={16}>
                <Col md={12} xs={24}>
                  <Row gutter={16}>
                    <Col span={24}>
                      <FormItem
                        label={intl.formatMessage({ id: 'ui-general-released-at' })}
                        name='released_at'
                        required={values?.is_published}
                      >
                        <DatePicker
                          showToday
                          showTime
                          name='released_at'
                          format={dateFieldFormat}
                          disabledDate={(date) => isDateDisabled(
                            date,
                            [],
                            !isEmpty(values?.released_to)
                            && date.isAfter(moment(values?.released_to)),
                          )}
                          value={values?.released_at ? moment(values.released_at).utc(false) : null}
                          onChange={async (date, dateString) => {
                            await setFieldValue('released_at', !isEmpty(dateString) ? moment(dateString).utc(true) : '');
                          }}
                          onBlur={async ({ target }) => {
                            await setFieldTouched('released_at', true);
                            await onBlur('released_at', !isEmpty(target.value) ? moment(target.value).utc(true) : '', setFieldValue);
                          }}
                        />
                      </FormItem>
                    </Col>

                    <Col span={24}>
                      <FormItem
                        label={intl.formatMessage({ id: 'ui-general-released-to' })}
                        name='released_to'
                        required={values?.is_published}
                      >
                        <DatePicker
                          showToday
                          showTime
                          name='released_to'
                          format={dateFieldFormat}
                          disabledDate={(date) => isDateDisabled(
                            date,
                            [],
                            !isEmpty(values?.released_at) && date.isBefore(moment(values?.released_at)),
                          )}
                          value={values?.released_to ? moment(values.released_to).utc(false) : null}
                          onChange={async (date, dateString) => {
                            await setFieldValue('released_to', !isEmpty(dateString) ? moment(dateString).utc(true) : '');
                          }}
                          onBlur={async ({ target }) => {
                            await setFieldTouched('released_to', true);
                            await onBlur('released_to', !isEmpty(target.value) ? moment(target.value).utc(true) : '', setFieldValue);
                          }}
                        />
                      </FormItem>
                    </Col>

                    <Col span={24}>
                      <FormItem
                        label={intl.formatMessage({ id: 'ui-general-template' })}
                        name='template_id'
                        required
                      >
                        <Select
                          loading={false}
                          allowClear
                          disabled
                          name="template_id"
                          options={templateOptions}
                          placeholder={intl.formatMessage({ id: 'ui-general-choose-template' })}
                        />
                      </FormItem>
                    </Col>

                    <Col span={24}>
                      <FormItem
                        label={intl.formatMessage({ id: 'ui-general-preset' })}
                        extra={intl.formatMessage({ id: 'presets-template-hint' })}
                        name='preset_id'
                      >
                        <Select
                          loading={false}
                          allowClear
                          name="preset_id"
                          onChange={async (value) => {
                            await applyPreset(value, values, setFieldValue, presets, templateFields);
                          }}
                          options={mkTemplatesOptions(presets)}
                          placeholder={intl.formatMessage({ id: 'ui-general-choose-preset' })}
                        />
                      </FormItem>
                    </Col>

                    <Col span={24}>
                      <Collapse accordion className='hp-mb-24'>
                        <Panel
                          key="additional-info"
                          header={
                            <Title className='collapse-item-title' level={5}>
                              {intl.formatMessage({ id: 'ui-general-optional-data' })}
                            </Title>
                          }
                        >
                          <Row gutter={16}>
                            <Col span={24}>
                              <TitleWithAutomaticGeneration useAutomaticGeneration={false} />
                            </Col>
                            <Col span={24}>
                              <FormItem
                                name='use_utc_zero_time'
                                label={
                                  <Row className='w-full' justify="space-between" align="middle">
                                    <IntlMessages id="ui-general-use-utc" />
                                    <Tooltip
                                      placement="top"
                                      title={
                                        <>
                                          <IntlMessages id="ui-general-use-utc-info" />
                                          <div>
                                            <Link to='https://x-flow-ltd.atlassian.net/wiki/spaces/HCD/pages/734461960/Use+UTC+0' target="_blank">
                                              <u><IntlMessages id="ui-general-documentation-link" /></u>
                                            </Link>
                                          </div>
                                        </>
                                      }
                                    >
                                      <InfoCircleOutlined className='hp-ml-8' />
                                    </Tooltip>
                                  </Row>
                                }
                              >
                                <Switch name="use_utc_zero_time" />
                              </FormItem>
                            </Col>
                          </Row>
                        </Panel>
                      </Collapse>
                    </Col>
                  </Row>

                  <Divider />
                  <Title className="hp-mb-24" level={4}><IntlMessages id="ui-general-template-data" /></Title>
                  {templateFields.length ? (
                    <Row>
                      <Col span={12}>
                        <DynamicFormDataFields
                          setFieldValue={setFieldValue}
                          fields={mainFields}
                          values={values}
                          span={24}
                        />
                        {!!optionalFields.length && (
                          <Collapse>
                            <Panel
                              key="1"
                              header={
                                <Title level={5} className='collapse-item-title'>
                                  <IntlMessages id="ui-general-additional-parameters" />
                                </Title>
                              }
                            >
                              <DynamicFormDataFields
                                setFieldValue={setFieldValue}
                                fields={optionalFields}
                                values={values}
                                span={24}
                              />
                            </Panel>
                          </Collapse>
                        )}
                      </Col>
                    </Row>
                  ) : <Row gutter={16} className="hp-d-flex-justify-center"><Spin spinning /></Row>}
                </Col>
                <Col md={12} xs={24}>
                  <PrivateComponent allowedPermissions={[ Permissions.CONSTRUCTORS.TEMPLATES.VISUALIZATIONS.VIEW_NEWS_VISUALIZATIONS ]}>
                    <TemplateVisualization
                      isLoading={isLoadingVisualizations}
                      currentValues={values}
                      visualizations={visualizations}
                      dynamicData={templateFields}
                    />
                  </PrivateComponent>
                </Col>
              </Row>

              <Divider />
              <Title className="hp-mb-24" level={4}><IntlMessages id="ui-general-targets" /></Title>

              <Row gutter={16}>
                <Col span={12}>
                  <FormItem
                    label={intl.formatMessage({ id: 'ui-general-min-version' })}
                    name='target.min_version'
                  >
                    <Input
                      name="target.min_version"
                      placeholder={intl.formatMessage({ id: 'ui-general-targets-min-version-placeholder' })}
                    />
                  </FormItem>
                </Col>

                <Col span={12}>
                  <FormItem
                    label={intl.formatMessage({ id: 'ui-general-max-version' })}
                    name='target.max_version'
                  >
                    <Input
                      name="target.max_version"
                      placeholder={intl.formatMessage({ id: 'ui-general-targets-max-version-placeholder' })}
                    />
                  </FormItem>
                </Col>
                <Col span={24}>
                  <FormItem
                    label={intl.formatMessage({ id: 'ui-general-platform' })}
                    name='target.platform'
                  >
                    <Select
                      mode="multiple"
                      allowClear
                      name="target.platform"
                      options={targetPlatformOptions}
                      placeholder={intl.formatMessage({ id: 'ui-general-choose-platform' })}
                    />
                  </FormItem>
                </Col>

                <Col span={24}>
                  <FormItem
                    label={intl.formatMessage({ id: 'ui-general-countries' })}
                    name='target.countries'
                    required
                  >
                    <CountrySelector
                      onSelectAll={async () => {
                        await setFieldValue('target.countries', countries.map((country) => country.value).sort());
                      }}
                      isDisabledSelectAll={values?.target?.countries?.length === countries.length}
                      name='target.countries'
                      placeholder={intl.formatMessage({ id: 'ui-general-choose-countries' })}
                      setFieldValue={setFieldValue}
                      setFieldTouched={setFieldTouched}
                    />
                  </FormItem>
                </Col>
              </Row>


              {(isDirty || hasUnpublishedChanges) && (
                <>
                  <Alert
                    message={<IntlMessages id="ui-general-edit-has-changes" />}
                    type="info"
                    showIcon
                  />

                  <Row justify='end' className='hp-mt-24 hp-mb-24'>
                    <Col>
                      <Row>
                        <IntlMessages id="features-edit-publish-to-hc" />
                        <Switch name="is_published" className='hp-ml-16' />
                      </Row>
                    </Col>
                  </Row>
                </>
              )}

              <Row gutter={[ 16, 16 ]} className='hp-mt-16' justify='end'>
                <Col>
                  <ResetButton
                    disabled={false}
                    onClick={() => resetForm(initialValues)}
                  >
                    <IntlMessages id='ui-general-reset' />
                  </ResetButton>
                </Col>

                <Col>
                  <SubmitButton
                    type="primary"
                    loading={isSubmitting}
                    disabled={!isValid || !isDirty}
                  >
                    <IntlMessages id="ui-general-save" />
                  </SubmitButton>
                </Col>
              </Row>
            </Spinner>
          </Form>
        );
      }}
    </Formik>
  );
};

FormEditDynamicNews.propTypes = {
  initialErrors: PropTypes.object.isRequired,
  initialValues: PropTypes.object.isRequired,
  templateOptions: PropTypes.array.isRequired,
  templateFields: PropTypes.array.isRequired,
  validationSchema: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
};


export default FormEditDynamicNews;
